import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, List, ListItemText, ListItemIcon,
  ListItemButton, Avatar, Chip, Button,
} from '@mui/material';
import {
  Medication as MedicationIcon, Warning as AllergyIcon, Sick as IllnessIcon,
  LocalHospital as ProviderIcon,
  Phone as PhoneIcon, Email as EmailIcon,
  Checkroom as CheckroomIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard, SearchInput } from '../../components/common';
import { MedicationFormDialog, AllergyFormDialog, IllnessFormDialog, HealthcareProviderFormDialog, ClothingSizesDialog } from '../../components/dialogs';
import type { HealthcareProviderData } from '../../components/dialogs';
import { useFamilyMembersData, useFamilyData } from '../../hooks';
import { usePersonData } from '../../hooks/usePersonData';
import { useFamily } from '../../context';
import { colors } from '../../theme';
import { insertPersonData, updateData, deletePersonData } from '../../services/dataApi';

const HEALTHCARE_CATEGORIES = ['doctor', 'medical', 'dentist', 'specialist', 'therapist', 'pediatrician', 'optometrist', 'dermatologist', 'orthodontist'];

const getCategoryColor = (category?: string): string => {
  switch (category?.toLowerCase()) {
    case 'doctor':
    case 'medical':
    case 'pediatrician':
      return '#EF4444';
    case 'dentist':
    case 'orthodontist':
      return '#3B82F6';
    case 'specialist':
      return '#8B5CF6';
    case 'therapist':
      return '#10B981';
    case 'optometrist':
      return '#F59E0B';
    case 'dermatologist':
      return '#EC4899';
    default:
      return '#6366F1';
  }
};

const HealthPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { memberList, getMemberName, getMemberColor, isLoading: familyLoading } = useFamily();
  const memberIds = useMemo(() => memberList.map((m) => m.id), [memberList]);

  const [search, setSearch] = useState('');
  const [filterMemberId, setFilterMemberId] = useState('');

  // Dialog states
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  const [illnessDialogOpen, setIllnessDialogOpen] = useState(false);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<any>(null);
  const [selectedAllergy, setSelectedAllergy] = useState<any>(null);
  const [selectedIllness, setSelectedIllness] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);

  const skipHealthQueries = familyLoading || memberIds.length === 0;

  // Clothing sizes
  const [clothingSizesDialogOpen, setClothingSizesDialogOpen] = useState(false);
  const [clothingSizesMemberId, setClothingSizesMemberId] = useState<string | null>(null);
  const clothingSizesMemberName = clothingSizesMemberId ? getMemberName(clothingSizesMemberId) : '';

  // Clothing sizes data for the currently selected member
  const {
    items: clothingSizesItems,
    insert: insertClothingSizes,
    update: updateClothingSizes,
  } = usePersonData(clothingSizesMemberId || undefined, 'clothing_sizes', { skip: !clothingSizesMemberId, useSubscription: false });

  // Fetch clothing sizes for all members to show summary
  const { items: allClothingSizes } = useFamilyMembersData(memberIds, 'clothing_sizes', { skip: skipHealthQueries });
  const { items: medications, loading: medsLoading } = useFamilyMembersData(memberIds, 'medication', { skip: skipHealthQueries });
  const { items: allergies, loading: allergiesLoading } = useFamilyMembersData(memberIds, 'allergy', { skip: skipHealthQueries });
  const { items: illnesses, loading: illnessesLoading } = useFamilyMembersData(memberIds, 'illness', { skip: skipHealthQueries });

  // Healthcare providers from contacts
  const { items: contacts, loading: contactsLoading, insert: insertContact, update: updateContact, remove: removeContact } = useFamilyData('contact');

  const providers = useMemo(() => {
    return contacts.filter((item: any) => {
      const category = (item.data?.data?.category || item.data?.data?.role || '').toLowerCase();
      return category && HEALTHCARE_CATEGORIES.some(hc => category.includes(hc));
    });
  }, [contacts]);

  const loading = medsLoading || allergiesLoading || illnessesLoading || contactsLoading;

  // Filter helpers
  const searchLower = search.toLowerCase();
  const filteredMeds = medications.filter((med: any) => {
    const d = (med.data?.data || {}) as any;
    if (filterMemberId && med.person_id !== filterMemberId) return false;
    if (search && !(d.name || '').toLowerCase().includes(searchLower)) return false;
    return true;
  });
  const filteredAllergies = allergies.filter((a: any) => {
    const d = (a.data?.data || {}) as any;
    if (filterMemberId && a.person_id !== filterMemberId) return false;
    if (search && !(d.name || '').toLowerCase().includes(searchLower)) return false;
    return true;
  });
  const filteredIllnesses = illnesses.filter((i: any) => {
    const d = (i.data?.data || {}) as any;
    if (filterMemberId && i.person_id !== filterMemberId) return false;
    if (search && !(d.name || '').toLowerCase().includes(searchLower)) return false;
    return true;
  });
  const filteredProviders = providers.filter((p: any) => {
    const d = (p.data?.data || {}) as any;
    if (!search) return true;
    const name = d.name || `${d.first || ''} ${d.last || ''}`.trim();
    return name.toLowerCase().includes(searchLower)
      || (d.category || '').toLowerCase().includes(searchLower)
      || (d.specialty || '').toLowerCase().includes(searchLower)
      || (d.organization || d.practice || '').toLowerCase().includes(searchLower);
  });

  // CRUD handlers
  const handleSaveMedication = async (data: any) => {
    if (selectedMedication?.id) {
      await updateData(selectedMedication.id, 'medication', data).then(() => enqueueSnackbar('Medication updated', { variant: 'success' }));
    } else {
      await insertPersonData(data.personId, 'medication', data).then(() => enqueueSnackbar('Medication added', { variant: 'success' }));
    }
  };
  const handleSaveAllergy = async (data: any) => {
    if (selectedAllergy?.id) {
      await updateData(selectedAllergy.id, 'allergy', data).then(() => enqueueSnackbar('Allergy updated', { variant: 'success' }));
    } else {
      await insertPersonData(data.personId, 'allergy', data).then(() => enqueueSnackbar('Allergy added', { variant: 'success' }));
    }
  };
  const handleSaveIllness = async (data: any) => {
    if (selectedIllness?.id) {
      await updateData(selectedIllness.id, 'illness', data).then(() => enqueueSnackbar('Illness updated', { variant: 'success' }));
    } else {
      await insertPersonData(data.personId, 'illness', data).then(() => enqueueSnackbar('Illness added', { variant: 'success' }));
    }
  };
  const handleDelete = async (id: string, type: string) => {
    await deletePersonData(id).then(() => enqueueSnackbar(`${type} deleted`, { variant: 'success' }));
  };
  const handleSaveProvider = async (data: HealthcareProviderData, familyIds: string[]) => {
    if (selectedProvider?.id) {
      await updateContact(selectedProvider.id, data);
      enqueueSnackbar('Provider updated', { variant: 'success' });
    } else {
      for (const familyId of familyIds) {
        await insertContact(data, familyId);
      }
      enqueueSnackbar('Provider added', { variant: 'success' });
    }
  };
  const handleDeleteProvider = async () => {
    if (!selectedProvider) return;
    await removeContact(selectedProvider.id);
    enqueueSnackbar('Provider deleted', { variant: 'success' });
  };

  if (loading) return <LoadingSpinner message="Loading health data..." />;

  return (
    <Box>
      <PageHeader title="Health" subtitle="Track medications, allergies, illnesses, and healthcare providers" />

      {/* Search & Member Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search health records..." />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip label="All" onClick={() => setFilterMemberId('')} variant={!filterMemberId ? 'filled' : 'outlined'} color={!filterMemberId ? 'primary' : 'default'} sx={{ cursor: 'pointer' }} />
          {memberList.map((member) => {
            const name = getMemberName(member.id);
            return (
              <Chip key={member.id} avatar={<Avatar sx={{ bgcolor: getMemberColor(member.id) }}>{name.charAt(0)}</Avatar>} label={name} onClick={() => setFilterMemberId(filterMemberId === member.id ? '' : member.id)} variant={filterMemberId === member.id ? 'filled' : 'outlined'} color={filterMemberId === member.id ? 'primary' : 'default'} sx={{ cursor: 'pointer' }} />
            );
          })}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard title="Medications" icon={MedicationIcon} iconColor={colors.health} value={medications.length} onClick={() => { setSelectedMedication(null); setMedicationDialogOpen(true); }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard title="Allergies" icon={AllergyIcon} iconColor="#FF9800" value={allergies.length} onClick={() => { setSelectedAllergy(null); setAllergyDialogOpen(true); }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard title="Illnesses" icon={IllnessIcon} iconColor="#F44336" value={illnesses.length} onClick={() => { setSelectedIllness(null); setIllnessDialogOpen(true); }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard title="Providers" icon={ProviderIcon} iconColor="#6366F1" value={providers.length} onClick={() => { setSelectedProvider(null); setProviderDialogOpen(true); }} />
        </Grid>

        {/* Medications */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Medications</Typography>
              {filteredMeds.length === 0 ? (
                <EmptyState icon={MedicationIcon} title="No medications" description="Track medications for family members" actionLabel="Add Medication" onAction={() => { setSelectedMedication(null); setMedicationDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {filteredMeds.map((med: any) => {
                    const d = (med.data?.data || {}) as any;
                    const secondary = [getMemberName(med.person_id), d.dosage, d.frequency].filter(Boolean).join(' · ');
                    return (
                      <ListItemButton key={med.id} sx={{ px: 0, borderRadius: 1 }} onClick={() => { setSelectedMedication(med); setMedicationDialogOpen(true); }}>
                        <ListItemIcon>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: getMemberColor(med.person_id), fontSize: '0.75rem' }}>{getMemberName(med.person_id).charAt(0)}</Avatar>
                        </ListItemIcon>
                        <ListItemText primary={d.name || 'Unknown'} secondary={secondary} />
                        {d.pharmacy && <Chip label={d.pharmacy} size="small" variant="outlined" sx={{ ml: 0.5 }} />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Allergies */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Allergies</Typography>
              {filteredAllergies.length === 0 ? (
                <EmptyState icon={AllergyIcon} title="No allergies" description="Track allergies for family members" actionLabel="Add Allergy" onAction={() => { setSelectedAllergy(null); setAllergyDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {filteredAllergies.map((allergy: any) => {
                    const d = (allergy.data?.data || {}) as any;
                    return (
                      <ListItemButton key={allergy.id} sx={{ px: 0, borderRadius: 1 }} onClick={() => { setSelectedAllergy(allergy); setAllergyDialogOpen(true); }}>
                        <ListItemIcon>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: getMemberColor(allergy.person_id), fontSize: '0.75rem' }}>{getMemberName(allergy.person_id).charAt(0)}</Avatar>
                        </ListItemIcon>
                        <ListItemText primary={d.name || 'Unknown'} secondary={getMemberName(allergy.person_id)} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {d.epi_pen && <Chip label="EpiPen" size="small" color="error" />}
                          {d.severity && <Chip label={d.severity} size="small" color={d.severity === 'severe' ? 'error' : 'warning'} />}
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Illnesses */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Illnesses</Typography>
              {filteredIllnesses.length === 0 ? (
                <EmptyState icon={IllnessIcon} title="No illnesses" description="Track illnesses for family members" actionLabel="Add Illness" onAction={() => { setSelectedIllness(null); setIllnessDialogOpen(true); }} />
              ) : (
                <List disablePadding>
                  {filteredIllnesses.map((illness: any) => {
                    const d = (illness.data?.data || {}) as any;
                    return (
                      <ListItemButton key={illness.id} sx={{ px: 0, borderRadius: 1 }} onClick={() => { setSelectedIllness(illness); setIllnessDialogOpen(true); }}>
                        <ListItemIcon>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: getMemberColor(illness.person_id), fontSize: '0.75rem' }}>{getMemberName(illness.person_id).charAt(0)}</Avatar>
                        </ListItemIcon>
                        <ListItemText primary={d.name || 'Unknown'} secondary={`${getMemberName(illness.person_id)}${d.doctor ? ` · Dr. ${d.doctor}` : ''}`} />
                        {d.recovered ? <Chip label="Recovered" size="small" color="success" /> : d.startDate || d.start_date ? <Chip label="Active" size="small" color="warning" /> : null}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Clothing Sizes */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Clothing Sizes</Typography>
              {memberList.length === 0 ? (
                <EmptyState icon={CheckroomIcon} title="No family members" description="Add family members to track clothing sizes" />
              ) : (
                <Grid container spacing={2}>
                  {memberList.map((member) => {
                    const name = getMemberName(member.id);
                    const color = getMemberColor(member.id);
                    const sizesRecord = allClothingSizes.find((item: any) => item.person_id === member.id);
                    const latestEntry = sizesRecord?.data?.data?.entries?.[0];
                    const summary = latestEntry
                      ? [latestEntry.shirt && `Shirt: ${latestEntry.shirt}`, latestEntry.shoe && `Shoe: ${latestEntry.shoe}`, latestEntry.pants_waist && `Pants: ${latestEntry.pants_waist}`].filter(Boolean).join(' / ') || 'Sizes recorded'
                      : 'No sizes yet';
                    return (
                      <Grid key={member.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card
                          variant="outlined"
                          sx={{ cursor: 'pointer', '&:hover': { borderColor: color, boxShadow: 1 }, transition: 'all 0.2s' }}
                          onClick={() => {
                            setClothingSizesMemberId(member.id);
                            setClothingSizesDialogOpen(true);
                          }}
                        >
                          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                            <Avatar sx={{ bgcolor: color, width: 40, height: 40, fontSize: '1rem' }}>
                              {name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600}>{name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{summary}</Typography>
                            </Box>
                            <CheckroomIcon sx={{ color: 'text.disabled' }} />
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Healthcare Providers */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Healthcare Providers</Typography>
              {filteredProviders.length === 0 ? (
                <EmptyState icon={ProviderIcon} title="No healthcare providers" description="Add your family's doctors, dentists, and specialists" actionLabel="Add Provider" onAction={() => { setSelectedProvider(null); setProviderDialogOpen(true); }} />
              ) : (
                <Grid container spacing={2}>
                  {filteredProviders.map((provider: any) => {
                    const d = (provider.data?.data || {}) as any;
                    const name = d.name || `${d.first || ''} ${d.last || ''}`.trim() || 'Unknown';
                    const category = d.category || d.role || '';
                    const catColor = getCategoryColor(category);
                    return (
                      <Grid key={provider.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card
                          variant="outlined"
                          sx={{ cursor: 'pointer', '&:hover': { borderColor: catColor, boxShadow: 1 }, transition: 'all 0.2s' }}
                          onClick={() => { setSelectedProvider(provider); setProviderDialogOpen(true); }}
                        >
                          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: '16px !important' }}>
                            <Avatar sx={{ bgcolor: `${catColor}20`, color: catColor, width: 40, height: 40 }}>
                              <ProviderIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>{name}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                {d.specialty || category}
                              </Typography>
                              {(d.organization || d.practice) && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {d.organization || d.practice}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                {d.phone && (
                                  <Chip icon={<PhoneIcon sx={{ fontSize: 14 }} />} label={d.phone} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                )}
                                {d.email && (
                                  <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={d.email} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <MedicationFormDialog open={medicationDialogOpen} onClose={() => { setMedicationDialogOpen(false); setSelectedMedication(null); }} onSave={handleSaveMedication} onDelete={selectedMedication ? () => handleDelete(selectedMedication.id, 'Medication') : undefined} medication={selectedMedication} />
      <AllergyFormDialog open={allergyDialogOpen} onClose={() => { setAllergyDialogOpen(false); setSelectedAllergy(null); }} onSave={handleSaveAllergy} onDelete={selectedAllergy ? () => handleDelete(selectedAllergy.id, 'Allergy') : undefined} allergy={selectedAllergy} />
      <IllnessFormDialog open={illnessDialogOpen} onClose={() => { setIllnessDialogOpen(false); setSelectedIllness(null); }} onSave={handleSaveIllness} onDelete={selectedIllness ? () => handleDelete(selectedIllness.id, 'Illness') : undefined} illness={selectedIllness} />
      <HealthcareProviderFormDialog open={providerDialogOpen} onClose={() => { setProviderDialogOpen(false); setSelectedProvider(null); }} onSave={handleSaveProvider} onDelete={selectedProvider ? handleDeleteProvider : undefined} provider={selectedProvider} />

      {clothingSizesDialogOpen && clothingSizesMemberId && (
        <ClothingSizesDialog
          open={clothingSizesDialogOpen}
          onClose={() => { setClothingSizesDialogOpen(false); setClothingSizesMemberId(null); }}
          onSave={async (sizesData) => {
            const existing = clothingSizesItems[0];
            if (existing) {
              await updateClothingSizes(existing.data.id, sizesData);
            } else {
              await insertClothingSizes(sizesData);
            }
            enqueueSnackbar('Clothing sizes saved', { variant: 'success' });
          }}
          personName={clothingSizesMemberName}
          data={clothingSizesItems[0]?.data?.data || null}
        />
      )}
    </Box>
  );
};

export default HealthPage;
